import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Phone, PhoneCall, PhoneOff, PhoneIncoming, PhoneOutgoing,
  Clock, User, Activity, Play, Square, Trash2, RefreshCw,
  TrendingUp, AlertCircle, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const LiveCallMonitor = () => {
  const { user } = useAuth();
  const [liveCalls, setLiveCalls] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [stats, setStats] = useState({
    active_calls: 0,
    completed_today: 0,
    avg_duration_seconds: 0,
    failed_today: 0
  });
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState('knowlarity');

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      if (autoRefresh) {
        fetchData();
      }
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchData = async () => {
    try {
      const [liveResponse, historyResponse, eventsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/calls/live`),
        axios.get(`${API}/calls/live/history?limit=20`),
        axios.get(`${API}/webhooks/events?limit=50`),
        axios.get(`${API}/calls/live/stats`),
      ]);

      setLiveCalls(liveResponse.data);
      setCallHistory(historyResponse.data);
      setWebhookEvents(eventsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const simulateCall = async () => {
    try {
      const response = await axios.post(`${API}/calls/live/simulate?provider=${selectedProvider}`);
      toast.success(`Call simulation started: ${response.data.call_id}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to simulate call');
    }
  };

  const updateCallStatus = async (callId, status) => {
    try {
      await axios.put(`${API}/calls/live/${callId}/update-status?status=${status}`);
      toast.success(`Call status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const clearSimulatedCalls = async () => {
    try {
      const response = await axios.delete(`${API}/calls/live/clear-simulated`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear simulated calls');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ringing: 'bg-amber-100 text-amber-700 border-amber-200',
      ongoing: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      completed: 'bg-slate-100 text-slate-700 border-slate-200',
      failed: 'bg-rose-100 text-rose-700 border-rose-200',
    };
    return colors[status] || colors.completed;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ringing':
        return <PhoneIncoming className="animate-pulse" size={16} />;
      case 'ongoing':
        return <PhoneCall size={16} />;
      case 'completed':
        return <CheckCircle size={16} />;
      case 'failed':
        return <XCircle size={16} />;
      default:
        return <Phone size={16} />;
    }
  };

  const getCallDuration = (startedAt) => {
    if (!startedAt) return '0s';
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);

    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="live-call-monitor-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Live Call Monitor
            </h1>
            <p className="text-slate-600">Real-time call tracking and webhook events</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'border-emerald-600 text-emerald-700' : ''}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw size={16} className={autoRefresh ? 'text-emerald-600 animate-spin' : ''} />
              <span className="ml-2">{autoRefresh ? 'Live' : 'Paused'}</span>
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="manual-refresh-button">
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border border-emerald-200 bg-emerald-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <PhoneCall size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.active_calls}</p>
                <p className="text-xs text-emerald-600">Active Calls</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-blue-200 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.completed_today}</p>
                <p className="text-xs text-blue-600">Completed Today</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-purple-200 bg-purple-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{formatDuration(stats.avg_duration_seconds)}</p>
                <p className="text-xs text-purple-600">Avg Duration</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border border-rose-200 bg-rose-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <XCircle size={20} className="text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-rose-700">{stats.failed_today}</p>
                <p className="text-xs text-rose-600">Failed Today</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Simulation Controls (Admin Only) */}
        {user?.role === 'admin' && (
          <Card className="p-4 border border-indigo-200 bg-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Play size={20} className="text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Demo Mode</h3>
                  <p className="text-xs text-slate-600">Simulate calls for testing without real telephony</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger className="w-36" data-testid="provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="knowlarity">Knowlarity</SelectItem>
                    <SelectItem value="ozonetel">Ozonetel</SelectItem>
                    <SelectItem value="generic">Generic</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={simulateCall} 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="simulate-call-btn"
                >
                  <Play size={16} className="mr-2" />
                  Simulate Call
                </Button>
                <Button 
                  variant="outline" 
                  onClick={clearSimulatedCalls}
                  className="border-slate-300"
                  data-testid="clear-simulated-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Clear Simulated
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Live Calls Section */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Active Calls
              </h2>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                {liveCalls.length} active
              </Badge>
            </div>
            {liveCalls.length > 0 && (
              <div className="flex items-center gap-2 text-emerald-600">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium">Live</span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-slate-400" size={24} />
            </div>
          ) : liveCalls.length > 0 ? (
            <div className="space-y-4">
              {liveCalls.map((call) => (
                <Card
                  key={call.id}
                  className={`p-4 border-2 shadow-sm ${
                    call.status === 'ringing' 
                      ? 'border-amber-300 bg-amber-50' 
                      : 'border-emerald-300 bg-emerald-50'
                  }`}
                  data-testid={`live-call-${call.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${
                          call.status === 'ringing' ? 'bg-amber-100' : 'bg-emerald-100'
                        }`}>
                          {call.status === 'ringing' 
                            ? <PhoneIncoming size={24} className="text-amber-600 animate-pulse" />
                            : <PhoneCall size={24} className="text-emerald-600" />
                          }
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">
                            Call ID: {call.call_id}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(call.status)}`}>
                              {getStatusIcon(call.status)}
                              {call.status}
                            </span>
                            {call.metadata?.simulated && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                Simulated
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Caller</p>
                          <p className="text-sm font-medium text-slate-900">{call.caller_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Receiver</p>
                          <p className="text-sm font-medium text-slate-900">{call.receiver_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Duration</p>
                          <p className="text-sm font-bold text-emerald-700">{getCallDuration(call.started_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Provider</p>
                          <p className="text-sm font-medium text-slate-900 capitalize">{call.provider_id}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons for simulated calls */}
                    {user?.role === 'admin' && call.metadata?.simulated && (
                      <div className="flex flex-col gap-2 ml-4">
                        {call.status === 'ringing' && (
                          <Button
                            size="sm"
                            onClick={() => updateCallStatus(call.call_id, 'ongoing')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`answer-${call.call_id}`}
                          >
                            <PhoneCall size={14} className="mr-1" />
                            Answer
                          </Button>
                        )}
                        {call.status === 'ongoing' && (
                          <Button
                            size="sm"
                            onClick={() => updateCallStatus(call.call_id, 'completed')}
                            className="bg-slate-600 hover:bg-slate-700 text-white"
                            data-testid={`end-${call.call_id}`}
                          >
                            <Square size={14} className="mr-1" />
                            End Call
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCallStatus(call.call_id, 'failed')}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`fail-${call.call_id}`}
                        >
                          <XCircle size={14} className="mr-1" />
                          Fail
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PhoneOff size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 mb-2">No active calls at the moment</p>
              {user?.role === 'admin' && (
                <p className="text-sm text-slate-500">Use the Demo Mode above to simulate test calls</p>
              )}
            </div>
          )}
        </Card>

        {/* Tabs for History and Webhooks */}
        <Tabs defaultValue="history" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="history" data-testid="history-tab">
              <Clock size={16} className="mr-2" />
              Call History ({callHistory.length})
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="webhooks-tab">
              <Activity size={16} className="mr-2" />
              Webhook Events ({webhookEvents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-4">
            <Card className="p-6 border border-slate-200 shadow-sm">
              {callHistory.length > 0 ? (
                <div className="space-y-2">
                  {callHistory.map((call) => (
                    <div
                      key={call.id}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`call-history-${call.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${
                          call.status === 'completed' ? 'bg-emerald-50' : 
                          call.status === 'failed' ? 'bg-rose-50' : 'bg-slate-100'
                        }`}>
                          {getStatusIcon(call.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {call.call_id}
                          </p>
                          <p className="text-xs text-slate-600">
                            {call.caller_number} → {call.receiver_number}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">
                            {call.duration ? formatDuration(call.duration) : '-'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(call.started_at).toLocaleTimeString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(call.status)}`}>
                          {getStatusIcon(call.status)}
                          {call.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock size={40} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-600">No call history available</p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="webhooks" className="mt-4">
            <Card className="p-6 border border-slate-200 shadow-sm">
              {webhookEvents.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {webhookEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`webhook-event-${event.id}`}
                    >
                      <div className="p-2 bg-indigo-50 rounded-lg flex-shrink-0">
                        <Activity size={14} className="text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-medium text-slate-900 text-sm">{event.event_type}</p>
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs capitalize">
                            {event.provider_id}
                          </span>
                          {event.payload?.simulated && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              Demo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 truncate">Call: {event.call_id}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(event.received_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity size={40} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-600">No webhook events received yet</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure your telephony provider to send events to the webhook URL
                  </p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Integration Info */}
        <Card className="p-6 border border-blue-200 bg-blue-50">
          <h3 className="font-semibold text-slate-900 mb-3">How Live Call Monitoring Works</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-700">
            <div>
              <p className="font-medium mb-1">For Real Calls:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Configure your telephony provider (Knowlarity/Ozonetel) in Integrations</li>
                <li>Set up the webhook URL in your provider's dashboard</li>
                <li>Calls will appear here automatically when received</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-1">For Testing (Demo Mode):</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Use the "Simulate Call" button to create test calls</li>
                <li>Control call status (answer, end, fail) manually</li>
                <li>Clear simulated data when done testing</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default LiveCallMonitor;
